import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { ARRANGEMENTS, PACKAGES } from 'components/paperwork/content';
import { EQUIPMENT, STATE_EDITIONS } from 'data/paperworkVariants';

export const PAPERWORK_CHAPTERS = Object.freeze([
  { id: 'overview', label: 'Overview' }, { id: 'policies', label: 'Policies' },
  { id: 'procedures', label: 'Procedures' }, { id: 'forms', label: 'Forms' },
  { id: 'workbooks', label: 'Workbooks' },
]);
const FIELDS = ['tier', 'arrangement', 'equipment', 'state'];
const CHAPTER_FIELDS = ['problem', 'consequence', 'scope', 'workingArrangement', 'packageCovers', 'offerNote'];
const ROOT = '/api/site/v1/paperwork/content';
const CHANGE_KEY = 'magellan-paperwork-content-changed';
const CHANGE_EVENT = 'magellan:paperwork-content-changed';
const entries = new Map();
const IDLE = Object.freeze({ status: 'idle', data: null });
const MAX_CACHE_ENTRIES = 16;
let watchers = 0;

const validText = value => typeof value === 'string' && value.length <= 20000;
function chapters(value) {
  if (!value || typeof value !== 'object') throw new Error('Paperwork details unavailable');
  return Object.freeze(Object.fromEntries(PAPERWORK_CHAPTERS.map(({ id }) => {
    const chapter = value[id];
    if (!chapter || !CHAPTER_FIELDS.every(field => validText(chapter[field]))) throw new Error('Paperwork details unavailable');
    return [id, Object.freeze(Object.fromEntries(CHAPTER_FIELDS.map(field => [field, chapter[field]])))];
  })));
}
function selectionKey(selection) {
  if (!selection) return 'packages';
  if (!PACKAGES.some(item => item.id === selection.tier)
    || !ARRANGEMENTS.some(item => item.id === selection.arrangement)
    || !EQUIPMENT.some(item => item.id === selection.equipment)
    || !STATE_EDITIONS.some(item => item.code === selection.state)) return null;
  return FIELDS.map(field => selection[field]).join(':');
}
function entryFor(key) {
  if (!entries.has(key)) entries.set(key, { key, state: IDLE, listeners: new Set(), request: null, refreshAfter: false });
  const entry = entries.get(key);
  // Move the selected entry to the newest position, retaining at most a small
  // set of inactive variants rather than every possible state/equipment pair.
  entries.delete(key); entries.set(key, entry);
  trimCache(entry);
  return entry;
}
function trimCache(protectedEntry) {
  for (const [key, entry] of entries) {
    if (entries.size <= MAX_CACHE_ENTRIES) break;
    if (entry !== protectedEntry && !entry.request && !entry.listeners.size) entries.delete(key);
  }
}
function update(entry, state) {
  entry.state = Object.freeze(state);
  entry.listeners.forEach(listener => listener());
}
function readResponse(data, key) {
  if (!Array.isArray(data?.packages) || data.packages.length !== PACKAGES.length) throw new Error('Paperwork package information unavailable');
  const packages = PACKAGES.map(({ id, name }) => {
    const item = data.packages.find(pkg => pkg?.id === id);
    if (!item || item.name !== name || !validText(item.title) || !item.title.trim()
      || !validText(item.description) || !item.description.trim()) throw new Error('Paperwork package information unavailable');
    return Object.freeze({ id, name, title: item.title, description: item.description, chapters: chapters(item.chapters) });
  });
  if (key === 'packages') return Object.freeze({ packages });
  const expected = key.split(':');
  if (!data.selection || !FIELDS.every((field, index) => data.selection[field] === expected[index])) throw new Error('Selected paperwork information unavailable');
  return Object.freeze({ packages, selection: Object.freeze({ ...data.selection }), chapters: chapters(data.chapters) });
}
function load(entry) {
  if (entry.request) return entry.request;
  const query = entry.key === 'packages' ? '' : '?' + new URLSearchParams(Object.fromEntries(FIELDS.map((field, index) => [field, entry.key.split(':')[index]])));
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  // Publish the in-flight promise before notifying mounted page/chooser consumers.
  entry.request = Promise.resolve().then(async () => {
    const response = await fetch(ROOT + query, {
      method: 'GET', credentials: 'omit', cache: 'no-store', redirect: 'error', signal: controller.signal,
    });
    const result = await response.json();
    if (!response.ok || !result || !Object.hasOwn(result, 'data')) throw new Error('Paperwork information unavailable');
    return readResponse(result.data, entry.key);
  }).then(data => { if (!entry.refreshAfter) update(entry, { status: 'ready', data }); })
    .catch(() => { if (!entry.refreshAfter) update(entry, { status: 'error', data: null }); })
    .finally(() => {
      clearTimeout(timer);
      entry.request = null;
      if (entry.refreshAfter) {
        entry.refreshAfter = false;
        if (entry.listeners.size) load(entry);
        else entry.state = IDLE;
      }
      trimCache();
    });
  // No built-in copy or earlier variant is presented as a successful API read.
  update(entry, { status: 'loading', data: null });
  return entry.request;
}
function refreshMounted() {
  if (document.visibilityState === 'hidden') return;
  entries.forEach(entry => {
    if (entry.listeners.size) {
      if (entry.request) entry.refreshAfter = true;
      else load(entry);
    }
    else entry.state = IDLE;
  });
}
function storageChanged(event) { if (event.key === CHANGE_KEY) refreshMounted(); }
function watch() {
  watchers += 1;
  if (watchers === 1) {
    window.addEventListener('focus', refreshMounted);
    document.addEventListener('visibilitychange', refreshMounted);
    window.addEventListener('storage', storageChanged);
    window.addEventListener(CHANGE_EVENT, refreshMounted);
  }
  return () => {
    watchers -= 1;
    if (!watchers) {
      window.removeEventListener('focus', refreshMounted);
      document.removeEventListener('visibilitychange', refreshMounted);
      window.removeEventListener('storage', storageChanged);
      window.removeEventListener(CHANGE_EVENT, refreshMounted);
    }
  };
}

// Admin saves invalidate copy only. Prices and checkout stay on their existing API.
export function announcePaperworkContentChanged() {
  try { window.localStorage.setItem(CHANGE_KEY, String(Date.now())); } catch { /* Focus refresh remains available. */ }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function usePaperworkContent(selection = null, { enabled = true } = {}) {
  const key = enabled ? selectionKey(selection) : null;
  const entry = key ? entryFor(key) : null;
  const subscribe = useCallback(listener => {
    if (!entry) return () => {};
    entry.listeners.add(listener);
    return () => entry.listeners.delete(listener);
  }, [entry]);
  const snapshot = useCallback(() => entry?.state || IDLE, [entry]);
  const state = useSyncExternalStore(subscribe, snapshot, snapshot);
  useEffect(() => {
    if (!entry) return undefined;
    const stop = watch();
    load(entry);
    return stop;
  }, [entry]);
  const retry = useCallback(() => entry ? load(entry) : Promise.resolve(), [entry]);
  return { ...state, retry };
}
